import React, { useState, useEffect } from 'react';
import { MapPin, Building, Home, Save, Check, Edit3, X } from 'lucide-react';
import Card from '../../../components/ui/card';
import Button from '../../../components/ui/button';
import MapboxPlacesAutocomplete from '../../../components/ui/mapbox-places-autocomplete';

const OnboardingAddressCard = ({ client, clientDataHook }) => {
	const { updateCard, refreshData, isOnboarding, isLoading, isCardEditMode, toggleCardEditMode, cancelCardEdit } = clientDataHook;
	const cardName = 'addressInfo';
	const isEditing = isOnboarding || isCardEditMode(cardName);
	
	const [formData, setFormData] = useState({
		officeAddress: client?.officeAddress || null,
		homeAddress: client?.homeAddress || null,
	});
	const [saving, setSaving] = useState(false);
	const [saved, setSaved] = useState(false);

	// Refresh data when component mounts (for onboarding)
	useEffect(() => {
		if (isOnboarding && refreshData) {
			console.log('🟦 OnboardingAddressCard - Component mounted, refreshing data');
			refreshData();
		}
	}, [isOnboarding]); // Remove refreshData from dependencies to prevent infinite loop

	// Update form data when client prop changes (after API refresh)
	useEffect(() => {
		setFormData({
			officeAddress: client?.officeAddress || null,
			homeAddress: client?.homeAddress || null,
		});
	}, [client]);

	const handleAddressChange = (fieldKey, addressData) => {
		setFormData(prev => ({
			...prev,
			[fieldKey]: addressData
		}));
	};

	const handleSave = async () => {
		setSaving(true);
		setSaved(false);
		
		const dataToSend = {};
		if (formData.officeAddress) {
			dataToSend.officeAddress = formData.officeAddress;
		}
		if (formData.homeAddress) {
			dataToSend.homeAddress = formData.homeAddress;
		}
		
		try {
			const success = await updateCard(dataToSend);
			if (success) {
				setSaved(true);
				setTimeout(() => setSaved(false), 3000);
				if (!isOnboarding) {
					toggleCardEditMode(cardName); // Exit edit mode in profile view
				}
			}
		} catch (err) {
			console.error('Save error:', err);
		} finally {
			setSaving(false);
		}
	};
	
	const handleCancel = () => {
		// Reset form values to original client data
		setFormData({
			officeAddress: client?.officeAddress || null,
			homeAddress: client?.homeAddress || null,
		});
		cancelCardEdit(cardName);
	};

	const hasChanges = () => {
		const currentOffice = client?.officeAddress;
		const currentHome = client?.homeAddress;
		
		const officeChanged = JSON.stringify(formData.officeAddress) !== JSON.stringify(currentOffice);
		const homeChanged = JSON.stringify(formData.homeAddress) !== JSON.stringify(currentHome);
		
		return officeChanged || homeChanged;
	};

	const getDisplayValue = (address) => {
		if (!address) return null;
		if (typeof address === 'string') return address;
		return address.formatted_address || address.street || null;
	};

	return (
		<Card className='bg-white rounded-lg shadow-sm border'>
			{!isOnboarding && (
				<Card.Header className='flex flex-row items-center justify-center space-y-0 pb-4'>
					<div className='space-y-1 text-center'>
						<Card.Title className='flex items-center justify-center gap-2 text-lg font-montserrat-medium text-gray-900'>
							<MapPin className='h-5 w-5' />
							Contact & Address Information
						</Card.Title>
						<Card.Description className='text-sm text-gray-600'>
							Let us know where we can reach you and your office location.
						</Card.Description>
					</div>
					{!isEditing && (
						<Button
							size='sm'
							variant='outline'
							onClick={() => toggleCardEditMode(cardName)}
							className='border-gray-300 text-gray-600 hover:bg-gray-50 ml-4'>
							<Edit3 className='h-4 w-4 mr-2' />
							Edit
						</Button>
					)}
				</Card.Header>
			)}
			{isOnboarding && (
				<Card.Header>
					<Card.Title className='flex items-center gap-2'>
						<MapPin className='h-5 w-5' />
						Contact & Address Information
					</Card.Title>
					<Card.Description>
						Let us know where we can reach you and your office location.
					</Card.Description>
				</Card.Header>
			)}
			<Card.Content className='space-y-6'>
				{/* Office Address */}
				<div className='space-y-2'>
					<label className='text-sm font-semibold text-gray-800'>
						Office Address
					</label>
					
					{isEditing ? (
						<>
							<MapboxPlacesAutocomplete
								value={formData.officeAddress}
								onChange={(address) =>
									handleAddressChange('officeAddress', address)
								}
								placeholder='Enter your office address...'
								className='w-full'
							/>
							{getDisplayValue(formData.officeAddress) && (
								<p className='text-sm text-gray-600'>
									{getDisplayValue(formData.officeAddress)}
								</p>
							)}
						</>
					) : (
						<div className='flex items-start gap-2'>
							<Building className='h-4 w-4 text-gray-500 mt-0.5' />
							<div className='flex-1'>
								<span className='text-sm font-montserrat-regular text-gray-900'>
									{getDisplayValue(formData.officeAddress) || (
										<span className='text-gray-400 italic'>
											Enter your office address...
										</span>
									)}
								</span>
							</div>
						</div>
					)}
				</div>

				{/* Home Address */}
				<div className='space-y-2'>
					<label className='text-sm font-semibold text-gray-800'>
						Home Address
					</label>
					
					{isEditing ? (
						<>
							<MapboxPlacesAutocomplete
								value={formData.homeAddress}
								onChange={(address) => handleAddressChange('homeAddress', address)}
								placeholder='Enter your home address...'
								className='w-full'
							/>
							{getDisplayValue(formData.homeAddress) && (
								<p className='text-sm text-gray-600'>
									{getDisplayValue(formData.homeAddress)}
								</p>
							)}
						</>
					) : (
						<div className='flex items-start gap-2'>
							<Home className='h-4 w-4 text-gray-500 mt-0.5' />
							<div className='flex-1'>
								<span className='text-sm font-montserrat-regular text-gray-900'>
									{getDisplayValue(formData.homeAddress) || (
										<span className='text-gray-400 italic'>
											Enter your home address...
										</span>
									)}
								</span>
							</div>
						</div>
					)}
				</div>

				{/* Save/Cancel buttons when editing in profile mode */}
				{isEditing && !isOnboarding && (
					<div className='flex justify-end gap-3 pt-4 border-t border-gray-200'>
						<Button
							variant='outline'
							onClick={handleCancel}
							className='border-gray-300 text-gray-600 hover:bg-gray-50'>
							<X className='h-4 w-4 mr-2' />
							Cancel
						</Button>
						<Button
							onClick={handleSave}
							disabled={!hasChanges() || saving}
							className='bg-gray-900 hover:bg-gray-800 text-white'>
							{saving ? (
								<>
									<div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2'></div>
									Saving...
								</>
							) : (
								<>
									<Check className='h-4 w-4 mr-2' />
									Save Changes
								</>
							)}
						</Button>
					</div>
				)}
				
				{/* Save button for onboarding */}
				{isOnboarding && (
					<div className='pt-6 mt-6 border-t border-gray-200'>
						<Button
							onClick={handleSave}
							disabled={!hasChanges() || saving}
							className={`w-full ${
								hasChanges() && !saving
									? 'bg-gray-900 hover:bg-gray-800 text-white'
									: 'bg-gray-200 text-gray-500 cursor-not-allowed'
							}`}>
							{saving ? (
								<>
									<div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2'></div>
									Saving...
								</>
							) : saved ? (
								<>
									<Check className='h-4 w-4 mr-2' />
									Saved!
								</>
							) : (
								<>
									<Save className='h-4 w-4 mr-2' />
									Save & Continue
								</>
							)}
						</Button>
					</div>
				)}
			</Card.Content>
		</Card>
	);
};

export default OnboardingAddressCard;
